import { Capacitor, registerPlugin } from '@capacitor/core';

const AppUpdate = registerPlugin('AppUpdate');
let installedVersionPromise = null;

const isNativeAndroid = () => {
  try { return Capacitor.isNativePlatform?.() && Capacitor.getPlatform?.() === 'android'; } catch { return false; }
};

const parseVersion = (value) => {
  const text = String(value || '').trim().replace(/^v/i, '');
  if (!/^\d+(?:\.\d+){1,3}$/.test(text)) return null;
  return text.split('.').map((part) => Number(part));
};

export const isVersionBelow = (installed, minimum) => {
  const a = parseVersion(installed);
  const b = parseVersion(minimum);
  // Fail open on malformed configuration. An admin typo must never lock users
  // out of the app; Settings validates the same format before writing.
  if (!a || !b) return false;
  const size = Math.max(a.length, b.length);
  for (let index = 0; index < size; index += 1) {
    const left = a[index] || 0;
    const right = b[index] || 0;
    if (left !== right) return left < right;
  }
  return false;
};

export const getInstalledAppVersion = async () => {
  if (!isNativeAndroid()) return null;
  if (!installedVersionPromise) {
    installedVersionPromise = (async () => {
      try {
        const result = await AppUpdate.getAppVersion();
        return typeof result?.version === 'string' ? result.version : null;
      } catch {
        // Never use the hosted JS bundle's version here. It changes for every
        // installed shell and could misidentify an old binary as current.
        return null;
      }
    })();
  }
  return installedVersionPromise;
};

export const requiredUpdateForPassenger = async (notice) => {
  if (!isNativeAndroid() || !notice?.enabled || notice?.type !== 'required_update') return null;
  if (!['passenger', 'all'].includes(notice.audience)) return null;
  const minimumVersion = String(notice.passengerMinVersion || '').trim();
  // Invalid admin data is always fail-open. A real required-update setting,
  // however, intentionally treats an older shell without this native bridge
  // as unsupported — it can still use the HTTPS Play Store fallback below.
  if (!parseVersion(minimumVersion)) return null;
  const installedVersion = await getInstalledAppVersion();
  if (installedVersion && !isVersionBelow(installedVersion, minimumVersion)) return null;
  return {
    title: String(notice.title || '').trim().slice(0, 100) || 'Update required',
    message: String(notice.message || '').trim().slice(0, 500) || 'Update MyTownly to continue safely.',
    minimumVersion,
  };
};

export const startRequiredUpdate = async () => {
  try {
    const result = await AppUpdate.startImmediateUpdate();
    if (result?.started) return true;
  } catch (err) {
    console.warn('Immediate Play update unavailable:', err?.message || err);
  }
  try {
    await AppUpdate.openStoreListing();
    return true;
  } catch (err) {
    console.warn('Play Store fallback unavailable:', err?.message || err);
    // A web bundle may be newer than the native shell that introduced this
    // bridge. HTTPS navigation still gives that older shell a safe recovery
    // path instead of leaving an undismissible gate with a dead button.
    try {
      window.location.assign('https://play.google.com/store/apps/details?id=in.mytownly.rides');
      return true;
    } catch {
      return false;
    }
  }
};
